import {
  InfiniteData,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { productApiService } from 'api/services'
import { IDeleteProduct, IProduct, IUseDeleteProduct } from 'api/types/product'
import { QUERY_KEY } from 'app/constants'
import { AxiosError, AxiosResponse } from 'axios'
import { toastError } from 'components/toaster'
import { useTranslation } from 'react-i18next'
import { useUser } from './use-user'
import { useDebouncedUrlFilters } from 'lib/hooks/use-debounced-url-filters'
import { useUrlQuery } from 'lib/hooks/use-url-query'

export const useDeleteProduct = (): IUseDeleteProduct => {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const { user } = useUser()
  const urlFilters = useDebouncedUrlFilters()
  const { urlQuery } = useUrlQuery()

  const {
    mutate: deleteProductMutation,
    isPending,
    isError,
  } = useMutation({
    mutationKey: [QUERY_KEY.deleteProduct],
    mutationFn: ({ id }: IDeleteProduct) => productApiService.deleteProduct(id),
    onMutate: ({ id, subCategory }) => {
      const oldProducts = queryClient.getQueryData<
        AxiosResponse<Array<IProduct>>
      >([
        QUERY_KEY.products,
        {
          userId: user?.id ?? null,
          id: subCategory,
          filters: urlFilters,
          search: urlQuery?.id ?? '',
        },
      ])

      queryClient.setQueryData(
        [
          QUERY_KEY.products,
          {
            userId: user?.id ?? null,
            id: subCategory,
            filters: urlFilters,
            search: urlQuery?.id ?? '',
          },
        ],
        (oldData?: InfiniteData<AxiosResponse<Array<IProduct>>>) =>
          oldData && {
            ...oldData,
            pages: oldData.pages.map((item) => ({
              ...item,
              data: item.data.map((item) => {
                if (item.id === id) {
                  return {
                    ...item,
                    amount: 0,
                  }
                }

                return item
              }),
            })),
          },
      )

      return oldProducts
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        [
          QUERY_KEY.cart,
          {
            userId: user?.id ?? null,
            language: i18n.language,
          },
        ],
        data,
      )
    },
    onError: (error: AxiosError, variable, context) => {
      if (!context) return
      if (error.response) {
        toastError(t(`${error.response.data.error}`))
      }

      queryClient.setQueryData(
        [
          QUERY_KEY.products,
          {
            userId: user?.id ?? null,
            id: variable.subCategory,
            filters: urlFilters,
            search: urlQuery?.id ?? '',
          },
        ],
        context,
      )
    },
  })

  return {
    deleteProductMutation,
    isPending,
    isError,
  }
}
